import { useEffect, useState } from 'react';

export interface MapFocusPoint {
  lat: number;
  lng: number;
}

export interface MapFocus {
  coordinates: MapFocusPoint;
  /** A store is framed close enough to read its streets; the guest is only centered. */
  isSelectedStore: boolean;
}

interface UseMapFocusArgs {
  /** The guest's position: their fix, or the address they searched. */
  guestCoordinates?: MapFocusPoint | null;
  /** The selected store, once we know where it is. */
  selectedCoordinates?: MapFocusPoint | null;
  /** True while the selected store is still loading, so we don't know where it is yet. */
  isSelectionLoading?: boolean;
}

interface UseMapFocusResult {
  /** Where the map belongs, or null while we know of nowhere to put it. */
  focus: MapFocus | null;
  /**
   * Whether the map may be created yet. A map keeps the frame it was created with, so one
   * created while a selected store is still loading opens on the guest and then slides off
   * them — the jump a guest arriving on a store's link would otherwise be shown.
   */
  canOpenMap: boolean;
}

/**
 * Where the map should be pointed.
 *
 * Two things move the camera and they disagree. The guest's position moves it whenever it
 * changes — the first fix, a recenter, an address search — and so does the store they pick.
 * A selected store wins, because it is what the guest is looking at: a fix that lands after
 * a deep link into store details must not pull the map off that store.
 *
 * Dismissing a store leaves the map where it is. The guest closed a store; they did not ask
 * to be taken back to themselves.
 */
export function useMapFocus({
  guestCoordinates,
  selectedCoordinates,
  isSelectionLoading = false,
}: UseMapFocusArgs): UseMapFocusResult {
  const [focus, setFocus] = useState<MapFocus | null>(
    () => toFocus(selectedCoordinates, true) ?? toFocus(guestCoordinates, false)
  );

  useEffect(() => {
    if (selectedCoordinates) {
      setFocus(toFocus(selectedCoordinates, true));
    }
    // The object is rebuilt every render, so follow the coordinates themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCoordinates?.lat, selectedCoordinates?.lng]);

  useEffect(() => {
    // Deliberately keyed on the guest's coordinates alone: this has to run when they move,
    // and must not run again when a store is dismissed.
    if (guestCoordinates && !selectedCoordinates) {
      setFocus(toFocus(guestCoordinates, false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestCoordinates?.lat, guestCoordinates?.lng]);

  // Latched: the map is only ever held back from opening, never torn down and rebuilt for a
  // store that has to be fetched later. Moving the camera covers that case.
  const [canOpenMap, setCanOpenMap] = useState(!isSelectionLoading);

  useEffect(() => {
    if (!isSelectionLoading) {
      setCanOpenMap(true);
    }
  }, [isSelectionLoading]);

  return { focus, canOpenMap };
}

function toFocus(
  point: MapFocusPoint | null | undefined,
  isSelectedStore: boolean
): MapFocus | null {
  return point ? { coordinates: { lat: point.lat, lng: point.lng }, isSelectedStore } : null;
}
